import { useCallback, useEffect, useRef, useState } from 'react';
import { Endpoint } from '@/constants';
import { useShipping } from '@/contexts/shippingContext';
import type { InitResponse } from '@/globals';
import { useSendOrder } from '@/hooks/useSendOrder';
import { buildScaData } from './buildScaData';
import { useGetMerchantSessionKey } from './useGetMerchantSessionKey';
import { useLoadSagePay } from './useLoadSagePay';

const TOKENISATION_GUARD_TIMEOUT_MS = 30_000;

interface UseSagePayOptions {
  onThreeDSecureRequired?: (url: string) => void;
}

export const useSagePay = (options?: UseSagePayOptions) => {
  const [isInitialRenderComplete, setIsInitialRenderComplete] = useState(false);
  const [isCheckoutInitialized, setIsCheckoutInitialized] = useState(false);
  const checkoutRef = useRef<InitResponse>(null);

  const { isSagePayScriptLoaded } = useLoadSagePay();


  const [merchantSessionKey, setMerchantSessionKey] = useState('');
  
  // Refs to store latest values for callbacks
  const orderPayloadRef = useRef<any>(null);
  const merchantSessionKeyRef = useRef<string>('');
  const tokenizationMerchantKeyRef = useRef<string>('');

  // #2940 - submit guard covering the merchant-key fetch and the Opayo tokenisation
  // round trip. Everything used to be gated on useSendOrder's `isPending`, which does
  // not arm until the order POST starts, so a second tap during tokenisation ran the
  // whole sequence again with a fresh merchant session key and a fresh card
  // identifier - two distinct Opayo transactions and two charges (ES01041318 /
  // ES01041319, 24 Sep). The ref is what actually blocks the second tap: it is set
  // synchronously, whereas state would not be visible until the next render.
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const releaseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const releaseSubmitGuard = useCallback(() => {
    if (releaseTimeoutRef.current) {
      clearTimeout(releaseTimeoutRef.current);
      releaseTimeoutRef.current = null;
    }

    isSubmittingRef.current = false;
    setIsSubmitting(false);
  }, []);
  const { getMerchantSessionKey, isSuccess } = useGetMerchantSessionKey(
    setMerchantSessionKey,
  );

  const { orderPayload } = useShipping();

  const { isPending, sendOrder } = useSendOrder(Endpoint.CreateCreditCardOrder, {
    // Released on every outcome, including 3DS-required and a decline: a customer who
    // abandons the bank challenge or is declined must be able to pay again. On the
    // success path the page is already navigating, so releasing is a no-op.
    onSettled: () => releaseSubmitGuard(),
    onThreeDSecureRequired: options?.onThreeDSecureRequired
  });

  // Update refs whenever values change
  useEffect(() => {
    orderPayloadRef.current = orderPayload;
  }, [orderPayload]);

  useEffect(() => {
    merchantSessionKeyRef.current = merchantSessionKey;
  }, [merchantSessionKey]);

  const tokenisationHandler = useCallback(
    (tokenisationResult: {
      success: boolean;
      cardIdentifier: string;
    }) => {
      if (releaseTimeoutRef.current) {
        clearTimeout(releaseTimeoutRef.current);
        releaseTimeoutRef.current = null;
      }

      if (tokenisationResult.success) {
        
        // Always submit immediately after successful tokenization
        // Use the merchant key that was used for tokenization
        const currentOrderPayload = orderPayloadRef.current;
        const tokenizationKey = tokenizationMerchantKeyRef.current;
        
        console.info('useSagePay: Card tokenized, submitting with tokenization key', {
          hasOrderPayload: !!currentOrderPayload,
          merchantKeyLength: tokenizationKey?.length
        });
        
        sendOrder({
          cardIdentifier: tokenisationResult.cardIdentifier,
          merchantSessionKey: tokenizationKey,
          scaData: buildScaData(),
          ...currentOrderPayload,
        });
      } else {
        // Release before throwing: the throw escapes into Opayo's callback, so this is
        // the only chance to hand the button back to the customer.
        releaseSubmitGuard();

        throw new Error(
          'An error occurred processing your order. Please contact Easons Customer Services at http://www.easonhelpcentre.com/contact-us/.',
        );
      }
    },
    [releaseSubmitGuard, sendOrder],
  );

  const initialiseCheckout = useCallback(() => {
    if (checkoutRef.current) {
      console.warn(
        'SagePay Checkout is already initialized. Destroying previous instance.',
      );
      checkoutRef.current.destroy();
    }

    if (window.sagepayCheckout) {
      const newKey = getMerchantSessionKey(merchantSessionKey);

      if (isSuccess) {
        checkoutRef.current = window.sagepayCheckout({
          merchantSessionKey: newKey,
          containerSelector: '#credit-card-form',
          onTokenise: tokenisationHandler,
        });
        setIsCheckoutInitialized(true);
        console.info('SagePay Checkout initialized:', checkoutRef.current);
      }
    }
  }, [
    getMerchantSessionKey,
    isSuccess,
    merchantSessionKey,
    tokenisationHandler,
  ]);

  const createOrder = () => {
    console.info('useSagePay: createOrder called');
    
    if (!isSuccess || isPending || isSubmittingRef.current) {
      console.warn('useSagePay: Cannot proceed', {
        isSuccess,
        isPending,
        isSubmitting: isSubmittingRef.current,
      });
      return;
    }

    // Armed before the first await-shaped step, not after it.
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    // Watchdog: if Opayo never invokes onTokenise there is nothing else to clear the
    // guard, and the fix would trade a double charge for a customer who can never pay.
    // The window is ~50x a normal tokenisation, so it cannot race a live submission.
    releaseTimeoutRef.current = setTimeout(
      releaseSubmitGuard,
      TOKENISATION_GUARD_TIMEOUT_MS,
    );
    
    // Always get a fresh merchant key and tokenize
    const freshKey = getMerchantSessionKey(merchantSessionKeyRef.current);
    console.info('useSagePay: Fresh merchant key obtained');
    
    // Store the key used for tokenization
    tokenizationMerchantKeyRef.current = freshKey;
    
    // Always tokenize with the fresh key
    console.info('useSagePay: Tokenizing card with fresh merchant key');
    checkoutRef.current?.tokenise({
      newMerchantSessionKey: freshKey,
    });
  };

  useEffect(() => {
    if (
      isInitialRenderComplete &&
      isSagePayScriptLoaded &&
      !isCheckoutInitialized
    ) {
      initialiseCheckout();
    }
  }, [
    initialiseCheckout,
    isInitialRenderComplete,
    isSagePayScriptLoaded,
    isCheckoutInitialized,
  ]);


  useEffect(() => {
    setIsInitialRenderComplete(true);
  }, []);

  useEffect(
    () => () => {
      if (releaseTimeoutRef.current) {
        clearTimeout(releaseTimeoutRef.current);
      }
    },
    [],
  );

  // isSubmitting is folded in so PayNowButton disables during tokenisation too - the
  // button guard reads this same flag.
  return {
    createCreditCardOrder: createOrder,
    isPending: isPending || isSubmitting,
  };
};
